import React, { useEffect, useState } from 'react';
import { updateComplaint } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './ComplaintDescriptionEditor.css';

const ADMIN_ROLES = new Set(['admin', 'administrator', 'super_admin', 'superadmin']);

const isAdminRole = role => {
  const value = String(role || '').trim().toLowerCase();
  return ADMIN_ROLES.has(value) || value.includes('admin');
};

const isLocked = complaint => (
  complaint?.is_locked === true ||
  Number(complaint?.is_locked) === 1 ||
  String(complaint?.is_locked || '').trim().toLowerCase() === 'true'
);

const ComplaintDescriptionEditor = ({ complaint, displayClassName = '', onSaved }) => {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const status = String(complaint?.status || '').toUpperCase();
  const editableStatus = ['OPEN', 'ACCEPTED', 'DEFERRED'].includes(status);
  const canEdit = Boolean(complaint?.id) && editableStatus && (!isLocked(complaint) || isAdminRole(user?.role));

  useEffect(() => {
    setDraft(String(complaint?.defect_description || ''));
    setEditing(false);
  }, [complaint?.id, complaint?.defect_description]);

  const saveDescription = async () => {
    const description = draft.trim();
    if (!canEdit || !description || saving) return;
    setSaving(true);
    try {
      const response = await updateComplaint(complaint.id, {
        defect_description: description,
        action_type: 'DESCRIPTION',
        action_text: 'DEFECT DESCRIPTION UPDATED.'
      });
      const savedComplaint = response.data.complaint || { ...complaint, defect_description: description };
      setDraft(savedComplaint.defect_description || description);
      setEditing(false);
      await onSaved?.(savedComplaint);
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="complaint-description-display-row">
        <div className={displayClassName}>{complaint?.defect_description || '---'}</div>
        {canEdit ? (
          <button type="button" className="complaint-description-edit-btn" onClick={() => setEditing(true)}>
            Edit Description
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="complaint-description-editor">
      <textarea
        value={draft}
        onChange={event => setDraft(event.target.value)}
        rows="3"
        autoFocus
      />
      <div className="complaint-description-actions">
        <button type="button" onClick={() => { setDraft(complaint?.defect_description || ''); setEditing(false); }} disabled={saving}>Cancel</button>
        <button type="button" className="primary" onClick={saveDescription} disabled={saving || !draft.trim()}>
          {saving ? 'Saving...' : 'Save Description'}
        </button>
      </div>
    </div>
  );
};

export default ComplaintDescriptionEditor;
